import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronRight, ChevronsDownUp, ChevronsUpDown, FileText, Frame } from 'lucide-react';
import { Button } from '../shared/Button.jsx';
import { ScrollArea } from '../shared/ScrollArea.jsx';
import { firstEntryId } from './tree.js';
import { useHoverGlide } from './glide.jsx';

export function Sidebar({ tree, activeId, openKeys, onSelect, badgesFor }) {
  const [expanded, setExpanded] = useState(() => new Set(openKeys));
  const navRef = useRef(null);
  const scrollRef = useRef(null);
  const glowRef = useRef(null);
  useHoverGlide(scrollRef, glowRef, '.side-row, .side-story');

  // Open the active entry's ancestors when the selection changes elsewhere.
  useEffect(() => {
    if (!openKeys.length) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      for (const k of openKeys) next.add(k);
      return next;
    });
  }, [openKeys.join('|')]);

  useEffect(() => {
    const el = navRef.current?.querySelector('[aria-current="page"]');
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  const toggle = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <nav className="shell-side" aria-label="Components" ref={navRef}>
      <ScrollArea className="shell-side__scroll" viewportClassName="edl-scroll-fade" viewportRef={scrollRef}>
        <div ref={glowRef} className="edl-hover-glide" aria-hidden="true" />
        {tree.children.map((section) => (
          <section className="side-section" key={section.key}>
            {section.name && (
              <div className="side-section__head">
                <h2 className="edl-label side-section__label side-label">{section.name}</h2>
                <SectionToggle keys={collapsibleKeys(section)} expanded={expanded} setExpanded={setExpanded} name={section.name} />
              </div>
            )}
            <ul className="side-list">
              {(section.type === 'section' ? section.children : [section]).map((node) => (
                <TreeNode key={node.key} node={node} depth={0} expanded={expanded} toggle={toggle} activeId={activeId} onSelect={onSelect} badgesFor={badgesFor} />
              ))}
              {section.type === 'section' && section.entries?.length > 0 && (
                <TreeNode node={{ ...section, type: 'component', key: section.key + '#self' }} depth={0} expanded={expanded} toggle={toggle} activeId={activeId} onSelect={onSelect} badgesFor={badgesFor} />
              )}
            </ul>
          </section>
        ))}
      </ScrollArea>
    </nav>
  );
}

// The rows in a section that open and close: folders and components with
// stories (pages are plain links).
function collapsibleKeys(section) {
  const keys = [];
  const walk = (node) => {
    if (node.type === 'folder') {
      keys.push(node.key);
      node.children.forEach(walk);
    } else if (node.entries?.some((e) => e.type === 'story')) {
      keys.push(node.key);
    }
  };
  if (section.type === 'section') {
    section.children.forEach(walk);
    if (section.entries?.length) walk({ ...section, type: 'component', key: section.key + '#self' });
  } else {
    walk(section);
  }
  return keys;
}

// Collapse every row in the section, or expand them all once none is open.
function SectionToggle({ keys, expanded, setExpanded, name }) {
  if (!keys.length) return null;
  const anyOpen = keys.some((k) => expanded.has(k));
  const label = anyOpen ? 'Collapse all' : 'Expand all';
  return (
    <Button
      variant="ghost"
      size="compact"
      leadingIcon={anyOpen ? ChevronsDownUp : ChevronsUpDown}
      aria-label={`${label} in ${name}`}
      tooltip={label}
      onClick={() =>
        setExpanded((prev) => {
          const next = new Set(prev);
          for (const k of keys) anyOpen ? next.delete(k) : next.add(k);
          return next;
        })
      }
    />
  );
}

function TreeNode({ node, depth, expanded, toggle, activeId, onSelect, badgesFor }) {
  const open = expanded.has(node.key);

  if (node.type === 'folder') {
    return (
      <li className="side-item" data-open={open ? '' : undefined}>
        <button type="button" className="side-row side-row--folder" aria-expanded={open} onClick={() => toggle(node.key)} style={{ '--depth': depth }}>
          <span className="side-row__name side-label">{node.name}</span>
          <span className="side-row__toggle" aria-hidden="true">
            <Chevron open={open} />
          </span>
        </button>
        <div className="side-fold" inert={open ? undefined : true}>
          <div className="side-fold__clip">
            <ul className="side-list">
              {node.children.map((child) => (
                <TreeNode key={child.key} node={child} depth={depth + 1} expanded={expanded} toggle={toggle} activeId={activeId} onSelect={onSelect} badgesFor={badgesFor} />
              ))}
            </ul>
          </div>
        </div>
      </li>
    );
  }

  const stories = node.entries.filter((e) => e.type === 'story');
  const docs = node.entries.find((e) => e.type === 'docs');
  const isPage = stories.length === 0;
  const containsActive = node.entries.some((e) => e.id === activeId);
  const badges = badgesFor(docs ?? node.entries[0]);
  const target = docs?.id ?? firstEntryId(node);

  if (isPage) {
    return (
      <li className="side-item">
        <a
          href={`?path=/docs/${target}`}
          className={`side-row side-row--page${containsActive ? ' is-active' : ''}`}
          aria-current={containsActive ? 'page' : undefined}
          onClick={(e) => {
            e.preventDefault();
            onSelect(target);
          }}
          style={{ '--depth': depth }}
        >
          <ItemIcon type="docs" />
          <span className="side-row__name side-label">{node.name}</span>
          <Badges badges={badges} />
        </a>
      </li>
    );
  }

  return (
    <li className={`side-item${containsActive ? ' contains-active' : ''}`} data-open={open ? '' : undefined}>
      <div className={`side-row side-row--component${docs && docs.id === activeId ? ' is-active' : ''}${containsActive ? ' is-within' : ''}`} style={{ '--depth': depth }}>
        <a
          href={`?path=/docs/${target}`}
          className="side-row__link"
          aria-current={docs && docs.id === activeId ? 'page' : undefined}
          onClick={(e) => {
            e.preventDefault();
            if (!open) toggle(node.key);
            onSelect(target);
          }}
        >
          <ItemIcon type="docs" />
          <span className="side-row__name side-label">{node.name}</span>
        </a>
        <Badges badges={badges} />
        <button type="button" className="side-row__toggle" aria-expanded={open} aria-label={`${open ? 'Collapse' : 'Expand'} ${node.name}`} onClick={() => toggle(node.key)}>
          <Chevron open={open} />
        </button>
      </div>
      <div className="side-fold" inert={open ? undefined : true}>
        <div className="side-fold__clip">
          <ul className="side-list side-list--stories">
            <SidebarBranches count={stories.length} depth={depth} activeIndex={stories.findIndex((s) => s.id === activeId)} />
            {stories.map((s) => (
              <li key={s.id}>
                <a
                  href={`?path=/story/${s.id}`}
                  className={`side-story${s.id === activeId ? ' is-active' : ''}`}
                  aria-current={s.id === activeId ? 'page' : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    onSelect(s.id);
                  }}
                  style={{ '--depth': depth }}
                >
                  <ItemIcon type="story" />
                  <span className="side-label">{s.name}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </li>
  );
}

function SidebarBranches({ count, depth, activeIndex }) {
  const svgRef = useRef(null);
  // Paint the line undrawn, then mark the active path so the dash can travel.
  useLayoutEffect(() => {
    const root = svgRef.current;
    if (!root) return undefined;
    const paths = [...root.querySelectorAll('.side-branches__reach')];
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const apply = () => paths.forEach((path, k) => path.toggleAttribute('data-on', k === activeIndex));
    if (reduced) {
      apply();
      return undefined;
    }
    paths.forEach((path) => { void getComputedStyle(path).strokeDashoffset; });
    const id = requestAnimationFrame(apply);
    return () => cancelAnimationFrame(id);
  }, [activeIndex, count, depth]);

  if (count < 1) return null;
  // Same joints as a branched menu: a trunk under the parent icon, then a
  // quarter-circle into each child, ending just before its icon.
  const row = 28;
  const lift = 0;
  const r = 6;
  const trunk = 20 + depth * 16;
  const endX = 30 + depth * 16;
  const rowY = (k) => lift + k * row + row / 2;
  const branch = (k) => `M ${trunk} ${rowY(k) - r} A ${r} ${r} 0 0 0 ${trunk + r} ${rowY(k)} H ${endX}`;
  const reach = (k) => `M ${trunk} 0 V ${rowY(k) - r} A ${r} ${r} 0 0 0 ${trunk + r} ${rowY(k)} H ${endX}`;
  const length = (k) => rowY(k) - r + (Math.PI * r) / 2 + (endX - trunk - r);
  const height = lift + count * row;
  return (
    <svg ref={svgRef} className="side-branches" width={endX + 2} height={height} aria-hidden="true">
      <path className="side-branches__line" d={`M ${trunk} 0 V ${rowY(count - 1) - r}`} />
      {Array.from({ length: count }, (_, k) => (
        <path key={k} className="side-branches__line" d={branch(k)} />
      ))}
      {Array.from({ length: count }, (_, k) => {
        const len = length(k);
        return (
          <path
            key={k}
            className="side-branches__line side-branches__reach"
            d={reach(k)}
            style={{ strokeDasharray: len, '--len': `${len}px` }}
          />
        );
      })}
    </svg>
  );
}

function Badges({ badges }) {
  if (!badges?.length) return null;
  return (
    <span className="side-badges">
      {badges.map((b, i) => (
        <span key={i} className={`side-badge side-badge--${b.tone ?? 'outline'}${b.signal ? ` side-badge--signal-${b.signal}` : ''}`} title={b.title ?? b.label}>
          {b.short ?? b.label}
        </span>
      ))}
    </span>
  );
}

function ItemIcon({ type }) {
  const Icon = type === 'story' ? Frame : FileText;
  return <Icon className="side-glyph" size={16} strokeWidth={1.5} aria-hidden="true" />;
}

function Chevron({ open }) {
  return <ChevronRight className={`side-chevron${open ? ' is-open' : ''}`} aria-hidden="true" />;
}
